import React, { useState, useEffect } from 'react';

const AdminBilling = () => {
  // ... (Keep all existing state from your original file)
  const [activeTab, setActiveTab] = useState('transactions');
  const [dateFilter, setDateFilter] = useState('all');
  const [paymentModeFilter, setPaymentModeFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [viewMode, setViewMode] = useState('all');

  const [allPayments, setAllPayments] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    cashTotal: 0,
    upiTotal: 0,
    bankingTotal: 0,
    cardTotal: 0,
    transactionCount: 0
  });
  const [loading, setLoading] = useState(true);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMode: 'Cash',
    transactionReference: '',
    notes: ''
  });

  // NEW: Receipt modal state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [bookingReceipts, setBookingReceipts] = useState([]);
  const [selectedBookingForReceipts, setSelectedBookingForReceipts] = useState(null);

  // ... (Keep all existing useEffect and fetch functions)

  useEffect(() => {
    fetchPayments();
    fetchPendingPayments();
    fetchPaymentStats();
  }, []);

  useEffect(() => {
    if (dateFilter !== 'custom' || (customDateRange.start && customDateRange.end)) {
      fetchPaymentStats();
    }
  }, [dateFilter, customDateRange]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://hs.learninghub.ind.in/api/admin/payments');
      const data = await response.json();

      if (data.success) {
        setAllPayments(data.payments);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching payments:', error);
      alert('Error loading payments');
      setLoading(false);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      const response = await fetch('https://hs.learninghub.ind.in/api/admin/payments/pending');
      const data = await response.json();

      if (data.success) {
        setPendingPayments(data.pendingPayments);
      }
    } catch (error) {
      console.error('Error fetching pending payments:', error);
    }
  };

  const fetchPaymentStats = async () => {
    try {
      let url = 'https://hs.learninghub.ind.in/api/admin/payments/stats';
      const params = new URLSearchParams();

      if (dateFilter === 'custom' && customDateRange.start && customDateRange.end) {
        params.append('startDate', customDateRange.start);
        params.append('endDate', customDateRange.end);
      } else if (dateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.append('startDate', today);
        params.append('endDate', today);
      } else if (dateFilter === 'week') {
        const today = new Date();
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        params.append('startDate', weekAgo.toISOString().split('T')[0]);
        params.append('endDate', today.toISOString().split('T')[0]);
      } else if (dateFilter === 'month') {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        params.append('startDate', firstDay.toISOString().split('T')[0]);
        params.append('endDate', today.toISOString().split('T')[0]);
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching payment stats:', error);
    }
  };

  // ENHANCED: Payment recording with receipt notification
  // ENHANCED PAYMENT HANDLER WITH AUTO-DOWNLOAD
  // Replace the handleRecordPayment function in your adminbilling.js (around line 120-180)

  const handleRecordPayment = async (e) => {
    e.preventDefault();

    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (parseFloat(paymentForm.amount) > selectedBooking.balance_due) {
      alert(`Amount cannot exceed balance due (₹${selectedBooking.balance_due.toLocaleString('en-IN')})`);
      return;
    }

    try {
      const response = await fetch('https://hs.learninghub.ind.in/api/admin/payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingId: selectedBooking.booking_id,
          amount: parseFloat(paymentForm.amount),
          paymentMode: paymentForm.paymentMode,
          transactionReference: paymentForm.transactionReference,
          notes: paymentForm.notes
        })
      });

      const data = await response.json();

      if (data.success) {
        let successMessage = `✅ Payment recorded successfully!\nNew Balance: ₹${parseFloat(data.updatedBalance.balance_due).toLocaleString('en-IN')}`;

        // Check if receipt was generated
        if (data.receipt && data.receipt.success) {
          const invoiceNumber = data.receipt.invoiceNumber;
          successMessage += `\n\n📄 Receipt Generated: ${invoiceNumber}`;

          // ✨ AUTO-DOWNLOAD THE RECEIPT
          setTimeout(() => {
            console.log('🔽 Auto-downloading receipt:', invoiceNumber);
            window.open(`https://hs.learninghub.ind.in/api/admin/receipt/${invoiceNumber}`, '_blank');
          }, 500); // Small delay to ensure alert is shown first

        } else if (data.receiptError) {
          // Show error if receipt generation failed
          successMessage += `\n\n⚠️ Receipt generation failed: ${data.receiptError.message}`;
        }

        alert(successMessage);

        // Refresh data
        fetchPayments();
        fetchPendingPayments();
        fetchPaymentStats();

        // Close modal and reset form
        setShowPaymentModal(false);
        setSelectedBooking(null);
        setPaymentForm({
          amount: '',
          paymentMode: 'Cash',
          transactionReference: '',
          notes: ''
        });
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Error recording payment. Please try again.');
    }
  };

  // NEW: Fetch receipts for a booking
  const fetchBookingReceipts = async (bookingId, bookingCode) => {
    try {
      const response = await fetch(`https://hs.learninghub.ind.in/api/admin/booking/${bookingId}/receipts`);
      const data = await response.json();

      if (data.success) {
        setBookingReceipts(data.receipts);
        setSelectedBookingForReceipts(bookingCode);
        setShowReceiptModal(true);
      } else {
        alert('No receipts found for this booking');
      }
    } catch (error) {
      console.error('Error fetching receipts:', error);
      alert('Error loading receipts');
    }
  };

  // NEW: Download receipt
  const downloadReceipt = (invoiceNumber) => {
    window.open(`https://hs.learninghub.ind.in/api/admin/receipt/${invoiceNumber}`, '_blank');
  };

  const openPaymentModal = (booking) => {
    setSelectedBooking(booking);
    setPaymentForm({
      amount: booking.balance_due.toString(),
      paymentMode: 'Cash',
      transactionReference: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  // ... (Keep your existing filter functions)

  const getFilteredPayments = () => {
    let filtered = [...allPayments];

    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

    if (dateFilter === 'today') {
      filtered = filtered.filter(p => p.payment_date === today);
    } else if (dateFilter === 'yesterday') {
      filtered = filtered.filter(p => p.payment_date === yesterday);
    } else if (dateFilter === 'week') {
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
      filtered = filtered.filter(p => p.payment_date >= weekAgo);
    } else if (dateFilter === 'month') {
      const monthStart = new Date();
      monthStart.setDate(1);
      const monthStartStr = monthStart.toISOString().split('T')[0];
      filtered = filtered.filter(p => p.payment_date >= monthStartStr);
    } else if (dateFilter === 'custom' && customDateRange.start && customDateRange.end) {
      filtered = filtered.filter(p =>
        p.payment_date >= customDateRange.start && p.payment_date <= customDateRange.end
      );
    }

    if (paymentModeFilter !== 'all') {
      filtered = filtered.filter(p => p.payment_mode === paymentModeFilter);
    }

    return filtered;
  };

  const filteredPayments = getFilteredPayments();

  return (
    <div style={{ padding: '2rem', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* ... (Keep your existing header and tabs) */}

      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ margin: 0, color: '#1976d2', fontSize: '2rem' }}>
          💰 Billing & Payments
        </h1>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem' }}>
        <button
          onClick={() => setActiveTab('transactions')}
          style={{
            padding: '0.75rem 2rem',
            background: activeTab === 'transactions' ? '#1976d2' : 'white',
            color: activeTab === 'transactions' ? 'white' : '#333',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 600,
            transition: 'all 0.3s'
          }}
        >
          💳 Transactions
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            padding: '0.75rem 2rem',
            background: activeTab === 'pending' ? '#f44336' : 'white',
            color: activeTab === 'pending' ? 'white' : '#333',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 600,
            transition: 'all 0.3s',
            position: 'relative'
          }}
        >
          ⏰ Pending Payments
          {pendingPayments.length > 0 && (
            <span style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              background: '#ff5722',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}>
              {pendingPayments.length}
            </span>
          )}
        </button>
      </div>

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <>
          {/* Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '1.5rem',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Revenue</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', marginTop: '0.5rem' }}>
                ₹{stats.totalRevenue.toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.8 }}>
                {stats.transactionCount} transactions
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              padding: '1.5rem',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Cash Payments</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', marginTop: '0.5rem' }}>
                ₹{stats.cashTotal.toLocaleString('en-IN')}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              padding: '1.5rem',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>UPI Payments</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', marginTop: '0.5rem' }}>
                ₹{stats.upiTotal.toLocaleString('en-IN')}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              padding: '1.5rem',
              borderRadius: '12px',
              color: 'white'
            }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Card Payments</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', marginTop: '0.5rem' }}>
                ₹{stats.cardTotal.toLocaleString('en-IN')}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                style={{
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="week">Last 7 Days</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </select>

              {dateFilter === 'custom' && (
                <>
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                    style={{
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                  />
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                    style={{
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                  />
                </>
              )}

              <select
                value={paymentModeFilter}
                onChange={(e) => setPaymentModeFilter(e.target.value)}
                style={{
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Payment Modes</option>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Internet Banking">Internet Banking</option>
                <option value="Card">Card</option>
              </select>
            </div>
          </div>

          {/* Payments List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>Loading...</div>
          ) : filteredPayments.length > 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date</th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Booking</th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Guest</th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Room</th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Amount</th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Mode</th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '1rem' }}>
                        {new Date(payment.payment_date).toLocaleDateString('en-IN')}
                      </td>
                      <td style={{ padding: '1rem', fontWeight: 600 }}>
                        {payment.booking_code}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {payment.primary_guest_name}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {payment.room_number}
                      </td>
                      <td style={{ padding: '1rem', fontWeight: 700, color: '#4CAF50' }}>
                        ₹{parseFloat(payment.amount).toLocaleString('en-IN')}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          background: payment.payment_mode === 'Cash' ? '#ffebee' : '#e3f2fd',
                          color: payment.payment_mode === 'Cash' ? '#c62828' : '#1565c0'
                        }}>
                          {payment.payment_mode}
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <button
                          onClick={() => fetchBookingReceipts(payment.booking_dbid, payment.booking_code)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            transition: 'all 0.3s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.background = '#1565c0'}
                          onMouseOut={(e) => e.currentTarget.style.background = '#1976d2'}
                        >
                          📄 View Receipts
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{
              background: 'white',
              padding: '3rem',
              borderRadius: '12px',
              textAlign: 'center',
              color: '#666'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
              <p style={{ fontSize: '1.5rem', margin: 0 }}>No transactions found</p>
              <p style={{ fontSize: '1rem', marginTop: '0.5rem' }}>Try adjusting your filters</p>
            </div>
          )}
        </>
      )}

      {/* Pending Payments Tab */}
      {activeTab === 'pending' && (
        <>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>Loading...</div>
          ) : pendingPayments.length > 0 ? (
            <>
              {pendingPayments.map((booking, index) => (
                <div key={index} style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '1rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '2px solid #f44336'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                        Room {booking.room_number} - {booking.primary_guest_name}
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '1rem',
                        marginBottom: '1rem'
                      }}>
                        <div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                            Booking Code
                          </div>
                          <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                            {booking.booking_code}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                            Phone
                          </div>
                          <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                            {booking.guest_phone}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                            Stay Duration
                          </div>
                          <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                            {booking.number_of_nights} nights
                          </div>
                        </div>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '1rem',
                        padding: '1rem',
                        background: '#f9fafb',
                        borderRadius: '8px'
                      }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                            Total Amount
                          </div>
                          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#333' }}>
                            ₹{parseFloat(booking.total_amount).toLocaleString('en-IN')}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                            Paid
                          </div>
                          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#4CAF50' }}>
                            ₹{parseFloat(booking.advance_paid).toLocaleString('en-IN')}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                            Balance Due
                          </div>
                          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f44336' }}>
                            ₹{parseFloat(booking.balance_due).toLocaleString('en-IN')}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <button
                        style={{
                          padding: '1rem 2rem',
                          background: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          transition: 'all 0.3s'
                        }}
                        onClick={() => openPaymentModal(booking)}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(76, 175, 80, 0.3)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        💵 Record Payment
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div style={{
              background: 'white',
              padding: '3rem',
              borderRadius: '12px',
              textAlign: 'center',
              color: '#666',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
              <p style={{ fontSize: '1.5rem', margin: 0 }}>No pending payments</p>
              <p style={{ fontSize: '1rem', marginTop: '0.5rem' }}>All bookings are fully paid!</p>
            </div>
          )}
        </>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedBooking && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, color: '#1976d2' }}>
              💵 Record Payment - Room {selectedBooking.room_number}
            </h2>

            <div style={{
              padding: '1rem',
              background: '#f9fafb',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                Guest: <strong>{selectedBooking.primary_guest_name}</strong>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                Booking: <strong>{selectedBooking.booking_code}</strong>
              </div>
              <div style={{ fontSize: '1rem', marginTop: '0.75rem' }}>
                Outstanding Balance: <strong style={{ color: '#f44336', fontSize: '1.3rem' }}>
                  ₹{parseFloat(selectedBooking.balance_due).toLocaleString('en-IN')}
                </strong>
              </div>
            </div>

            <form onSubmit={handleRecordPayment}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Amount *
                </label>
                <input
                  type="number"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem'
                  }}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  required
                  min="1"
                  max={selectedBooking.balance_due}
                  step="0.01"
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Payment Mode *
                </label>
                <select
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                  value={paymentForm.paymentMode}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}
                  required
                >
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Internet Banking">Internet Banking</option>
                  <option value="Card">Card</option>
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Transaction Reference (Optional)
                </label>
                <input
                  type="text"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem'
                  }}
                  value={paymentForm.transactionReference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transactionReference: e.target.value })}
                  placeholder="e.g., UPI ID, Transaction ID"
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Notes (Optional)
                </label>
                <textarea
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  placeholder="e.g., Final payment, Partial payment, Room service charges"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#9e9e9e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                  onClick={() => setShowPaymentModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600,
                    transition: 'all 0.3s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  💵 Record ₹{paymentForm.amount ? parseFloat(paymentForm.amount).toLocaleString('en-IN') : '0'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NEW: Receipt Modal */}
      {showReceiptModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowReceiptModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, color: '#1976d2' }}>
              📄 Receipts for Booking {selectedBookingForReceipts}
            </h2>

            {bookingReceipts.length > 0 ? (
              <div style={{ marginTop: '1.5rem' }}>
                {bookingReceipts.map((receipt, index) => (
                  <div
                    key={index}
                    style={{
                      background: '#f9fafb',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                        {receipt.invoiceNumber}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        Date: {new Date(receipt.paymentDate).toLocaleDateString('en-IN')}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        Amount: ₹{receipt.amount.toLocaleString('en-IN')} ({receipt.paymentMode})
                      </div>
                    </div>
                    <button
                      onClick={() => downloadReceipt(receipt.invoiceNumber)}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        transition: 'all 0.3s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = '#1565c0'}
                      onMouseOut={(e) => e.currentTarget.style.background = '#1976d2'}
                    >
                      ⬇️ Download PDF
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
                <p>No receipts found for this booking</p>
              </div>
            )}

            <div style={{ marginTop: '2rem', textAlign: 'right' }}>
              <button
                onClick={() => setShowReceiptModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#9e9e9e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBilling;